import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * Users › Customer lookup (owner 2026-10-18; radius-wiki subscriber-cart-v1
 * §13): one box over mobile, name, email, address or PIN code, device id or
 * MAC (typed any way), equipment serial and ID proof number.
 *
 *   GET /access/operator/{ns}/subscribers/search?q=   subscribersearch:read
 *
 * radaccess v1.120. ID proof numbers arrive masked to their last 4.
 */
export interface SearchAddress {
  address: string;
  city: string;
  state: string;
  pin: string;
}

export interface SearchLogin {
  username: string;
  /** custuseracct.usertype: "primary" (the mobile), "onumac" (an ONU), ... */
  kind: string;
  plan: string;
  start: string;
  expiry: string;
  /** Whole days from the database clock to expiry; negative once expired; null without one. */
  days_left: number | null;
  installation_address: SearchAddress | null;
  /** The installation's radbilling location, which Edit profile corrects (radaccess v1.123); "" or absent without one. */
  installation_id?: string;
}

export interface SearchKYC {
  type: string;
  number: string;
  verified: boolean;
}

export interface SubscriberMatch {
  accountid: string;
  name: string;
  mobile: string;
  username: string;
  email: string;
  /** reg_phone 'alternate' (radaccess v1.123); absent from older radaccess. */
  alternate_mobile?: string;
  gender: string;
  status: string;
  registered: string;
  idprooftype: string;
  idproofnumber: string;
  kyc: SearchKYC[];
  billing_address: SearchAddress | null;
  logins: SearchLogin[];
  matched_on: string[];
  /** registration.email_verify = '1' (radaccess v1.125); absent from older radaccess. */
  email_confirmed?: boolean;
  /** The newest login-details event (radaccess v1.125); null: nothing recorded. */
  login_details?: LoginDelivery | null;
}

export interface LoginDelivery {
  event: "not_sent" | "confirm_sent" | "email_confirmed" | "sent";
  /** The database's wall clock, "YYYY-MM-DD HH:MM:SS". */
  at: string;
  actor: string;
}

export interface SearchResult {
  query: string;
  matches: SubscriberMatch[];
  more: boolean;
}

/** The server searches 3 to 100 characters. */
export const SEARCH_MIN = 3;

export function searchable(q: string): boolean {
  const n = [...q.trim()].length;
  return n >= SEARCH_MIN && n <= 100;
}

export async function searchSubscribers(identity: CallerIdentity, ns: string, q: string, signal?: AbortSignal): Promise<SearchResult> {
  const path = `/operator/${encodeURIComponent(ns)}/subscribers/search?q=${encodeURIComponent(q.trim())}`;
  const r = await callGateway<SearchResult>(identity, config().gatewayBase, path, signal ? { signal } : {});
  return { query: r.data?.query ?? q.trim(), matches: r.data?.matches ?? [], more: Boolean(r.data?.more) };
}

/** "Address, City, State PIN"; "" for none. */
export function addressLine(a: SearchAddress | null | undefined): string {
  if (!a) return "";
  const head = [a.address, a.city, a.state].map((x) => x.trim()).filter(Boolean).join(", ");
  return [head, a.pin.trim()].filter(Boolean).join(" ");
}

export type DaysTone = "expired" | "soon" | "ok" | "none";

/** How a login's days left reads: expired (<0), soon (0-3 days: recharge now), ok, or no plan. */
export function daysTone(days: number | null): DaysTone {
  if (days == null) return "none";
  if (days < 0) return "expired";
  if (days <= 3) return "soon";
  return "ok";
}

export function daysLabel(days: number | null): string {
  if (days == null) return "No plan";
  if (days < 0) return days === -1 ? "Expired 1 day ago" : `Expired ${-days} days ago`;
  if (days === 0) return "Expires today";
  return days === 1 ? "1 day left" : `${days} days left`;
}

/** The login kind as staff say it. */
export function loginKindLabel(kind: string): string {
  switch (kind) {
    case "primary":
      return "Mobile";
    case "onumac":
      return "ONU";
    default:
      return kind ? kind : "Device";
  }
}

/** The account's soonest expiry among its logins, for the card's headline; null when none has a plan. */
export function soonestDays(m: SubscriberMatch): number | null {
  const ds = m.logins.map((l) => l.days_left).filter((d): d is number => d != null);
  return ds.length ? Math.min(...ds) : null;
}
